import type { AdminLookupKind, AdminLookupResult } from '@/api/adminClient';

/** Group headings, in the order the palette shows them: what an id names first, then names and people. */
export const LOOKUP_KIND_LABELS: Record<AdminLookupKind, string> = {
  run: 'Runs',
  session: 'Sessions',
  project: 'Projects',
  microvm: 'MicroVMs',
  organization: 'Organizations',
  person: 'People',
};

const KIND_ORDER = Object.keys(LOOKUP_KIND_LABELS) as AdminLookupKind[];

export type LookupGroup = {
  kind: AdminLookupKind;
  label: string;
  items: Array<{ result: AdminLookupResult; index: number }>;
};

/** Results grouped by kind in {@link KIND_ORDER}; `index` is each result's place in the flat keyboard order. */
export function groupLookupResults(results: readonly AdminLookupResult[]): LookupGroup[] {
  const groups: LookupGroup[] = [];
  let index = 0;
  for (const kind of KIND_ORDER) {
    const matching = results.filter((result) => result.kind === kind);
    if (matching.length === 0) continue;
    groups.push({ kind, label: LOOKUP_KIND_LABELS[kind], items: matching.map((result) => ({ result, index: index++ })) });
  }
  return groups;
}

/** The results in the order the palette lists them, so arrow keys and Enter agree with what is on screen. */
export function orderedLookupResults(results: readonly AdminLookupResult[]): AdminLookupResult[] {
  return groupLookupResults(results).flatMap((group) => group.items.map((item) => item.result));
}

/** The highlighted row after an arrow key, wrapping at either end; -1 when there is nothing to highlight. */
export function moveHighlight(current: number, delta: 1 | -1, count: number): number {
  if (count <= 0) return -1;
  if (current < 0) return delta > 0 ? 0 : count - 1;
  return (current + delta + count) % count;
}

type ShortcutEvent = Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>;

/** Cmd+K on a Mac, Ctrl+K elsewhere: opens search from any page. */
export function isSearchShortcut(event: ShortcutEvent): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k';
}

/** How the shortcut is written on this computer. */
export function searchShortcutLabel(platform: string = typeof navigator === 'undefined' ? '' : navigator.platform): string {
  return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl K';
}

/** The page to open straight away: the one result whose id is exactly what was pasted. */
export function exactLookupMatch(query: string, results: readonly AdminLookupResult[]): AdminLookupResult | undefined {
  const wanted = query.trim().replace(/^[`'"]+|[`'"]+$/g, '').trim();
  if (!wanted) return undefined;
  const exact = results.filter((result) => result.id === wanted && result.kind !== 'person' && result.kind !== 'organization');
  return exact.length === 1 ? exact[0] : undefined;
}
